import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { PATHS } from "../../routes/paths";
import "./LoginStyles.css";

function MultiRoleLanding() {
  const navigate = useNavigate();
  const [selectedRole, setSelectedRole] = useState(null);
  const [loading, setLoading] = useState(false);

  // Role code -> route paths and display info
  const roleConfig = {
    R1: {
      path: PATHS.OWNER_DASHBOARD,
      title: "Business Owner",
      description: "Manage your business, locations, and staff",
      icon: "🏢",
    },
    R2: {
      path: PATHS.ADMIN_DASHBOARD,
      title: "Business Admin",
      description: "Manage operations and staff",
      icon: "⚙️",
    },
    R3: {
      path: "/dashboard/professional",
      title: "Professional",
      description: "Manage your schedule, services, and clients",
      icon: "💼",
    },
    R6: {
      path: PATHS.PORTAL_ADMIN_DASHBOARD,
      title: "Portal Admin",
      description: "Manage platform settings",
      icon: "👑",
    },
    R8: {
      path: PATHS.PORTAL_ADMIN_DASHBOARD,
      title: "Support Agent",
      description: "Handle support queues and customer resolution workflows",
      icon: "🛎️",
    },
  };

  // Read stored roles and setup status
  const token = localStorage.getItem("ogestyle_token");
  const hasCompletedSetup =
    localStorage.getItem("ogestyle_hasCompletedSetup") === "true";
  const rolesList = (() => {
    try {
      return JSON.parse(localStorage.getItem("ogestyle_roles") || "[]");
    } catch {
      return [];
    }
  })();

  // Check if user has R1 (Owner) role and hasn't completed setup yet
  const isOwner = rolesList.includes("R1");
  if (isOwner && !hasCompletedSetup) {
    // Redirect to shop registration instead of showing role selection
    navigate("/register-shop", { replace: true });
    return null;
  }

  // Build available roles
  const availableRoles = rolesList
    .map((code) => roleConfig[code])
    .filter(Boolean);

  const pickRole = async (targetRoleCode) => {
    try {
      setLoading(true);
      if (!token) {
        navigate("/login");
        return;
      }

      // Request a new token for the chosen role
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/switch-role`,
        { targetRoleCode },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const { token: newToken, role, roles } = res.data;

      // Persist
      localStorage.setItem("ogestyle_token", newToken);
      localStorage.setItem("ogestyle_role", role);
      localStorage.setItem("ogestyle_roles", JSON.stringify(roles || []));
      localStorage.setItem("ogestyle_role_override", role);

      // Navigate to the chosen dashboard
      let next = roleConfig[role]?.path || "/";

      // Check if R3 (professional) is actually a freelancer
      if (role === "R3") {
        try {
          const staffRes = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/api/staff/profile`,
            { headers: { Authorization: `Bearer ${newToken}` } },
          );
          if (staffRes.data?.staff?.isFreelancer) {
            next = "/dashboard/freelancer";
          }
        } catch (err) {
          // If API fails, use default R3 path
        }
      }

      navigate(next);
    } catch (err) {
      setLoading(false);
      alert(
        err?.response?.data?.message ||
          "Unable to switch role. Please try again.",
      );
    }
  };

  if (!token) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f5f5f5",
        }}
      >
        <div className="form-card classy-card" style={{ maxWidth: "400px" }}>
          <h2 className="classy-title">Session Expired</h2>
          <p className="classy-text">Please log in to continue.</p>
          <button onClick={() => navigate("/login")} className="classy-btn">
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (availableRoles.length === 0) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f5f5f5",
        }}
      >
        <div className="form-card classy-card" style={{ maxWidth: "400px" }}>
          <h2 className="classy-title">No Roles Available</h2>
          <p className="classy-text">Please contact support.</p>
          <button onClick={() => navigate("/login")} className="classy-btn">
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f5f5f5",
        padding: "20px",
      }}
    >
      <div
        className="form-card classy-card"
        style={{ maxWidth: "600px", width: "100%" }}
      >
        <div style={{ textAlign: "center", marginBottom: "30px" }}>
          <h2 className="classy-title" style={{ marginBottom: "10px" }}>
            Welcome Back!
          </h2>
          <p className="classy-text">
            You have multiple roles. Choose how you want to work right now.
          </p>
          <p style={{ color: "#666", fontSize: "14px", marginTop: "10px" }}>
            You can switch between roles anytime from your dashboard.
          </p>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {rolesList.map((roleCode) => {
            const config = roleConfig[roleCode];
            if (!config) return null;

            const isSelected = selectedRole === roleCode;

            return (
              <div
                key={roleCode}
                onClick={() => setSelectedRole(roleCode)}
                style={{
                  border: `2px solid ${isSelected ? "#003087" : "#ddd"}`,
                  borderRadius: "12px",
                  padding: "20px",
                  cursor: "pointer",
                  backgroundColor: isSelected ? "#f0f7ff" : "white",
                  transition: "all 0.2s",
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                }}
              >
                <div style={{ fontSize: "40px", flexShrink: 0 }}>
                  {config.icon}
                </div>
                <div style={{ flex: 1, textAlign: "left" }}>
                  <div
                    style={{
                      fontWeight: "600",
                      fontSize: "18px",
                      color: "#001d4a",
                      marginBottom: "4px",
                    }}
                  >
                    {config.title}
                  </div>
                  <div style={{ color: "#666", fontSize: "14px" }}>
                    {config.description}
                  </div>
                </div>
                <div style={{ flexShrink: 0 }}>
                  <div
                    style={{
                      width: "24px",
                      height: "24px",
                      borderRadius: "50%",
                      border: `2px solid ${isSelected ? "#003087" : "#ddd"}`,
                      backgroundColor: isSelected ? "#003087" : "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {isSelected && (
                      <div
                        style={{
                          color: "white",
                          fontSize: "16px",
                          fontWeight: "bold",
                        }}
                      >
                        ✓
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <button
          onClick={() => selectedRole && pickRole(selectedRole)}
          disabled={!selectedRole || loading}
          className="classy-btn"
          style={{
            marginTop: "24px",
            width: "100%",
            opacity: !selectedRole || loading ? 0.5 : 1,
            cursor: !selectedRole || loading ? "not-allowed" : "pointer",
          }}
        >
          {loading ? "Loading..." : "Continue"}
        </button>
      </div>
    </div>
  );
}

export default MultiRoleLanding;
